import React, { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/dataService';

const fmt = (n) => `₹${(n || 0).toFixed(2)}`;

const friendlyErr = (err) => {
  if (!err) return 'Something went wrong. Please try again.';
  const msg = String(err.message || err).toLowerCase();
  if (msg.includes('fetch') || msg.includes('network')) return 'Network error — check your internet connection.';
  if (msg.includes('jwt') || msg.includes('expired') || msg.includes('auth')) return 'Session expired — please refresh and log in again.';
  if (msg.includes('policy') || msg.includes('permission') || msg.includes('rls')) return "You don't have permission to do this.";
  if (msg.includes('duplicate') || msg.includes('unique')) return 'This record already exists.';
  if (msg.includes('foreign key') || msg.includes('violates foreign')) return 'This record is in use and cannot be removed.';
  if (msg.includes('timeout') || msg.includes('timed out')) return 'Request timed out — please try again.';
  return 'Something went wrong. Please try again.';
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const DISCOUNT_TYPES = ['NONE', 'PERCENT', 'FLAT'];

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: '9px',
  border: '1.5px solid #e5e7eb', fontSize: '0.9rem', outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
};

// ─── Customer Form Modal ────────────────────────────────────────────────────
const CustomerModal = ({ restaurantId, customer, onSave, onClose, canManageDiscounts = true }) => {
  const [form, setForm] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    email: customer?.email || '',
    is_whatsapp: customer?.is_whatsapp || false,
    discount_type: customer?.discount_type || 'NONE',
    discount_value: String(customer?.discount_value || 0),
    notes: customer?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      is_whatsapp: form.is_whatsapp,
      discount_type: form.discount_type,
      discount_value: form.discount_type === 'NONE' ? 0 : (parseFloat(form.discount_value) || 0),
      notes: form.notes.trim() || null,
    };
    let result;
    if (customer) {
      result = await crmService.updateCustomer(customer.id, payload);
    } else {
      result = await crmService.createCustomer(restaurantId, payload);
    }
    setSaving(false);
    if (result.error) { setError(friendlyErr(result.error)); return; }
    onSave(result.data);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '460px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '1.2rem', fontWeight: '700', color: '#333' }}>
          {customer ? 'Edit Customer' : 'Add Customer'}
        </h3>

        {[
          { key: 'name', label: 'Name *', placeholder: 'Full name' },
          { key: 'phone', label: 'Phone', placeholder: 'Phone number' },
          { key: 'email', label: 'Email', placeholder: 'Email address' },
        ].map(({ key, label, placeholder }) => (
          <div key={key} style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '5px' }}>{label}</label>
            <input type="text" placeholder={placeholder} value={form[key]} onChange={e => set(key, e.target.value)} style={inputStyle} />
          </div>
        ))}

        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', cursor: 'pointer', fontSize: '0.9rem', color: '#444' }}>
          <input type="checkbox" checked={form.is_whatsapp} onChange={e => set('is_whatsapp', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: '#25D366' }} />
          WhatsApp number
        </label>

        {/* Discount — hidden for managers */}
        {canManageDiscounts && <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '14px', marginBottom: '18px' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Auto Discount</div>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            {DISCOUNT_TYPES.map(t => (
              <button key={t} onClick={() => set('discount_type', t)} style={{ flex: 1, padding: '7px 4px', borderRadius: '7px', fontWeight: '600', fontSize: '0.78rem', cursor: 'pointer', border: 'none', background: form.discount_type === t ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#e5e7eb', color: form.discount_type === t ? 'white' : '#555' }}>
                {t === 'NONE' ? 'None' : t === 'PERCENT' ? '% Off' : '₹ Flat'}
              </button>
            ))}
          </div>
          {form.discount_type !== 'NONE' && (
            <div>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '5px' }}>
                {form.discount_type === 'PERCENT' ? 'Discount %' : 'Flat Amount (₹)'}
              </label>
              <input type="number" min="0" value={form.discount_value} onChange={e => set('discount_value', e.target.value)} style={{ ...inputStyle, width: '140px' }} />
              {form.discount_type !== 'NONE' && form.discount_value > 0 && (
                <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#FF6B00', fontWeight: '600' }}>
                  This discount auto-applies every time this customer is billed.
                </p>
              )}
            </div>
          )}
        </div>}

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '5px' }}>Notes</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes about this customer..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '14px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '12px', borderRadius: '10px', background: saving ? '#e5e7eb' : 'linear-gradient(135deg, #FF4444, #FF6B00)', color: saving ? '#9ca3af' : 'white', border: 'none', fontWeight: '700', fontSize: '0.95rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={onClose} style={{ padding: '12px 20px', borderRadius: '10px', background: 'transparent', color: '#666', border: '1.5px solid #e5e7eb', fontWeight: '600', fontSize: '0.95rem', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Customer Detail View ───────────────────────────────────────────────────
const CustomerDetail = ({ customer, restaurant, onEdit, onBack, onDelete }) => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const curr = restaurant?.default_currency || '₹';

  useEffect(() => {
    crmService.getCustomerOrders(customer.id).then(({ data }) => {
      setOrders(data || []);
      setLoading(false);
    });
  }, [customer.id]);

  const discountLabel = () => {
    if (customer.discount_type === 'NONE' || !customer.discount_value) return 'No discount';
    return customer.discount_type === 'PERCENT'
      ? `${customer.discount_value}% off every bill`
      : `₹${customer.discount_value} flat off every bill`;
  };

  const totalSpent = orders
    .filter(o => o.paid_at && !o.is_invalid)
    .reduce((s, o) => s + (o.total_payable || o.total_amount || 0), 0);

  return (
    <div>
      {/* Back */}
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#FF4444', fontWeight: '700', fontSize: '0.9rem', padding: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
        ← Back to Customers
      </button>

      {/* Customer card */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 6px 20px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.5rem', fontWeight: '800', color: '#333' }}>{customer.name}</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {customer.phone && (
                <span style={{ fontSize: '0.9rem', color: '#555' }}>
                  📞 {customer.phone}
                  {customer.is_whatsapp && <span style={{ marginLeft: '6px', background: '#dcfce7', color: '#166534', fontSize: '0.72rem', fontWeight: '700', padding: '1px 8px', borderRadius: '10px' }}>WhatsApp</span>}
                </span>
              )}
              {customer.email && <span style={{ fontSize: '0.9rem', color: '#555' }}>✉ {customer.email}</span>}
            </div>
            {customer.notes && <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#888', fontStyle: 'italic' }}>{customer.notes}</p>}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onEdit} style={{ padding: '9px 18px', borderRadius: '9px', background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', border: 'none', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer' }}>
              Edit
            </button>
            <button onClick={onDelete} style={{ padding: '9px 18px', borderRadius: '9px', background: 'transparent', color: '#dc2626', border: '1.5px solid #fca5a5', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer' }}>
              Delete
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: '16px', marginTop: '20px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Visits', value: orders.filter(o => !o.is_invalid).length },
            { label: 'Total Spent', value: `${curr}${totalSpent.toFixed(2)}` },
            { label: 'Since', value: fmtDate(customer.created_at) },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px 18px', minWidth: '120px' }}>
              <div style={{ fontSize: '0.72rem', color: '#888', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#333' }}>{value}</div>
            </div>
          ))}
          <div style={{ background: customer.discount_type !== 'NONE' ? '#fef3c7' : '#f8f9fa', borderRadius: '10px', padding: '12px 18px', minWidth: '140px', border: customer.discount_type !== 'NONE' ? '1px solid #fcd34d' : 'none' }}>
            <div style={{ fontSize: '0.72rem', color: customer.discount_type !== 'NONE' ? '#92400e' : '#888', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Auto Discount</div>
            <div style={{ fontSize: '0.95rem', fontWeight: '800', color: customer.discount_type !== 'NONE' ? '#92400e' : '#999' }}>{discountLabel()}</div>
          </div>
        </div>
      </div>

      {/* Order history */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: '700', color: '#333' }}>Order History</h3>
        {loading ? (
          <p style={{ color: '#aaa', textAlign: 'center', padding: '20px 0' }}>Loading...</p>
        ) : orders.length === 0 ? (
          <p style={{ color: '#aaa', textAlign: 'center', padding: '20px 0' }}>No orders yet</p>
        ) : (
          orders.map(order => {
            const isInvalid = !!order.is_invalid;
            return (
              <div key={order.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #f3f4f6', opacity: isInvalid ? 0.5 : 1 }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#333', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {order.invoice_number || order.order_number}
                    {isInvalid && <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.65rem', fontWeight: '700', padding: '1px 6px', borderRadius: '4px' }}>INVALID</span>}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '2px' }}>
                    {fmtDateTime(order.paid_at || order.created_at)}
                    {order.payment_method && <span> · {order.payment_method.toUpperCase()}</span>}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '2px' }}>
                    {(order.order_items || []).slice(0, 3).map(oi => `${oi.quantity}× ${oi.menu_items?.name}`).join(', ')}
                    {(order.order_items || []).length > 3 && ` +${order.order_items.length - 3} more`}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                  <div style={{ fontWeight: '800', fontSize: '1rem', color: isInvalid ? '#aaa' : '#FF4444', textDecoration: isInvalid ? 'line-through' : 'none' }}>
                    {fmt(order.total_payable || order.total_amount)}
                  </div>
                  {order.discount_amount > 0 && (
                    <div style={{ fontSize: '0.72rem', color: '#16a34a' }}>−{fmt(order.discount_amount)} disc.</div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Main CRMApp ────────────────────────────────────────────────────────────
const CRMApp = ({ restaurant, canManageDiscounts = true }) => {
  const [customers, setCustomers]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [search, setSearch]             = useState('');
  const [showModal, setShowModal]       = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const loadCustomers = useCallback(async (silent = false) => {
    if (!restaurant?.id) return;
    if (!silent) setRefreshing(true);
    const { data } = await crmService.getCustomers(restaurant.id);
    setCustomers(data || []);
    setLoading(false);
    setRefreshing(false);
  }, [restaurant?.id]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const handleSave = (saved) => {
    setShowModal(false);
    setEditingCustomer(null);
    loadCustomers(true);
    if (saved && selectedCustomer?.id === saved.id) setSelectedCustomer(saved);
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete ${customer.name}? Their order history will be preserved but the customer record will be removed.`)) return;
    await crmService.deleteCustomer(customer.id);
    setSelectedCustomer(null);
    loadCustomers(true);
  };

  const q = search.trim().toLowerCase();
  const filtered = !q ? customers : customers.filter(c =>
    c.name.toLowerCase().includes(q) ||
    (c.phone || '').toLowerCase().includes(q) ||
    (c.email || '').toLowerCase().includes(q)
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #FF4444', borderTop: '4px solid transparent', borderRadius: '50%', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
          <h2 style={{ color: '#333', margin: 0 }}>Loading Customers...</h2>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Detail view
  if (selectedCustomer) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '30px 20px' }}>
        <CustomerDetail
          customer={selectedCustomer}
          restaurant={restaurant}
          onEdit={() => { setEditingCustomer(selectedCustomer); setShowModal(true); }}
          onBack={() => setSelectedCustomer(null)}
          onDelete={() => handleDelete(selectedCustomer)}
        />
        {showModal && (
          <CustomerModal
            restaurantId={restaurant.id}
            customer={editingCustomer}
            onSave={handleSave}
            onClose={() => { setShowModal(false); setEditingCustomer(null); }}
            canManageDiscounts={canManageDiscounts}
          />
        )}
      </div>
    );
  }

  // List view
  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '1.8rem', fontWeight: '700', background: 'linear-gradient(135deg, #FF4444, #FF6B00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Customers
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            {filtered.length} customer{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => loadCustomers()}
            disabled={refreshing}
            style={{ background: refreshing ? '#e5e7eb' : 'transparent', color: refreshing ? '#9ca3af' : '#555', border: '1.5px solid #e5e7eb', borderRadius: '10px', padding: '10px 16px', fontSize: '0.9rem', fontWeight: '600', cursor: refreshing ? 'not-allowed' : 'pointer' }}
          >
            {refreshing ? 'Refreshing...' : '↻ Refresh'}
          </button>
          <button
            onClick={() => { setEditingCustomer(null); setShowModal(true); }}
            style={{ background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 3px 10px rgba(255,68,68,0.25)' }}
          >
            + Add Customer
          </button>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search by name, phone, or email..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1.5px solid #e5e7eb', fontSize: '0.92rem', outline: 'none', marginBottom: '24px', boxSizing: 'border-box', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
      />

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 40px', background: 'white', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>👥</div>
          <h3 style={{ color: '#333', margin: '0 0 8px 0', fontSize: '1.4rem', fontWeight: '600' }}>No customers yet</h3>
          <p style={{ color: '#666', margin: '0 0 24px 0' }}>
            {search ? 'No customers match your search' : 'Customers are added during billing or by clicking Add Customer'}
          </p>
          {!search && (
            <button onClick={() => { setEditingCustomer(null); setShowModal(true); }} style={{ background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', border: 'none', borderRadius: '10px', padding: '12px 24px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer' }}>
              + Add First Customer
            </button>
          )}
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 6px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr 80px', gap: '0', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', padding: '12px 20px' }}>
            {['Name', 'Phone', 'Email', 'Discount', ''].map((h, i) => (
              <div key={i} style={{ fontSize: '0.75rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
            ))}
          </div>

          {filtered.map((c, idx) => {
            const discountBadge = c.discount_type !== 'NONE' && c.discount_value > 0
              ? (c.discount_type === 'PERCENT' ? `${c.discount_value}%` : `₹${c.discount_value}`)
              : null;
            return (
              <div
                key={c.id}
                onClick={() => setSelectedCustomer(c)}
                style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr 80px', gap: '0', padding: '14px 20px', borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', alignItems: 'center' }}
                onMouseOver={e => e.currentTarget.style.background = '#fafafa'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: '700', color: '#333', fontSize: '0.95rem' }}>
                    {c.name}
                    {c.is_whatsapp && c.phone && <span style={{ marginLeft: '8px', background: '#dcfce7', color: '#166534', fontSize: '0.68rem', fontWeight: '700', padding: '1px 7px', borderRadius: '10px' }}>WA</span>}
                  </div>
                  {c.notes && <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{c.notes}</div>}
                </div>
                <div style={{ fontSize: '0.88rem', color: '#555' }}>{c.phone || '—'}</div>
                <div style={{ fontSize: '0.88rem', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email || '—'}</div>
                <div>
                  {discountBadge ? (
                    <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.78rem', fontWeight: '700', padding: '3px 10px', borderRadius: '20px' }}>{discountBadge} off</span>
                  ) : (
                    <span style={{ color: '#ccc', fontSize: '0.82rem' }}>None</span>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <button
                    onClick={e => { e.stopPropagation(); setEditingCustomer(c); setShowModal(true); }}
                    style={{ background: 'transparent', border: '1px solid #e5e7eb', borderRadius: '7px', padding: '5px 12px', fontSize: '0.8rem', color: '#555', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <CustomerModal
          restaurantId={restaurant.id}
          customer={editingCustomer}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingCustomer(null); }}
          canManageDiscounts={canManageDiscounts}
        />
      )}
    </div>
  );
};

export default CRMApp;
